import { useState, useEffect } from "react";
import Header from "../components/Header";
import Hero from "../sections/Hero";
import QuotationFirst from "../sections/QuotationFirst";
import QuotationSecond from "../sections/QuotationSecond";
import Industries from "../sections/Industries";
import Contact from "../sections/Contact";
import Education from "../sections/Education";
import Property from "../sections/Property";
import Technology from "../sections/Technology";
import Tourism from "../sections/Tourism";
import Sports from "../sections/Sports";
import Engineering from "../sections/Engineering";
import AboutPage from "./AboutPage";
import SocialMediaPage from "./SocialMediaPage";
import DashboardPage from "./DashboardPage";
import { useAuth } from "../context/AuthContext";
import { NeedsQuizModal } from "../components/NeedsQuizModal";
import { BubbleBackground } from "@/components/animate-ui/components/backgrounds/bubble";
import TechSaraf from "../components/TechSaraf";
import ProductCarousel from "../sections/ProductCarousel";
import { motion, AnimatePresence } from "motion/react";
import Footer from "@/components/Footer";

type Page = "home" | "contact" | "education" | "property" | "technology" | "tourism" | "sports" | "engineering" | "about" | "socials" | "dashboard";

const HASH_PAGES: Page[] = ["contact", "education", "property", "technology", "tourism", "sports", "engineering", "about", "socials", "dashboard"];

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const [currentPage, setCurrentPage] = useState<Page>("home");
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [pendingScrollTarget, setPendingScrollTarget] = useState<string | null>(null);
  const [prevAuth, setPrevAuth] = useState(isAuthenticated);
  const [bgReady, setBgReady] = useState(false);

  // Delay heavy backgrounds to optimize initial page load CPU (Lighthouse)
  useEffect(() => {
    const handleInteraction = () => {
      setBgReady(true);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };

    window.addEventListener('mousemove', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);
    window.addEventListener('scroll', handleInteraction);
    window.addEventListener('keydown', handleInteraction);

    // Fallback timer
    const fallbackTimer = setTimeout(() => {
      handleInteraction();
    }, 15000);

    return () => {
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      clearTimeout(fallbackTimer);
    };
  }, []);

  // Listen to custom event to open the quiz modal
  useEffect(() => {
    const handleOpenQuiz = () => {
      setIsQuizOpen(true);
    };
    window.addEventListener("open-quiz-modal", handleOpenQuiz);
    return () => window.removeEventListener("open-quiz-modal", handleOpenQuiz);
  }, []);

  // Sync auth state with redirection
  useEffect(() => {
    if (isAuthenticated && !prevAuth) {
      handleNavigate("dashboard");
    } else if (!isAuthenticated && prevAuth) {
      if (currentPage === "dashboard") {
        handleNavigate("home");
      }
    }
    setPrevAuth(isAuthenticated);
  }, [isAuthenticated, prevAuth, currentPage]);

  // Sync state with URL hash for navigation & refreshing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "") as Page;
      if (HASH_PAGES.includes(hash)) {
        setCurrentPage(hash);
        window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
      } else {
        setCurrentPage("home");
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();

    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  // Global synchronized logo shine clock
  useEffect(() => {
    const triggerShine = () => {
      document.body.classList.add("shining-now");
      const timer = setTimeout(() => {
        document.body.classList.remove("shining-now");
      }, 1500);
      return timer;
    };

    let activeTimer = triggerShine();
    const interval = setInterval(() => {
      activeTimer = triggerShine();
    }, 1800);

    return () => {
      clearInterval(interval);
      clearTimeout(activeTimer);
      document.body.classList.remove("shining-now");
    };
  }, []);

  // Handle pending scroll targets when transitioning back to home
  useEffect(() => {
    if (currentPage === "home" && pendingScrollTarget) {
      const timer = setTimeout(() => {
        const element = document.getElementById(pendingScrollTarget);
        if (element) element.scrollIntoView({ behavior: "smooth" });
        setPendingScrollTarget(null);
      }, 380);

      return () => clearTimeout(timer);
    }
  }, [currentPage, pendingScrollTarget]);

  const handleNavigate = (page: Page, targetId?: string) => {
    if (page !== "home") {
      window.location.hash = `#${page}`;
    } else {
      window.location.hash = "";
      window.history.pushState("", document.title, window.location.pathname);

      if (targetId) setPendingScrollTarget(targetId);

      if (currentPage !== "home") {
        setCurrentPage("home");
      } else {
        if (targetId) {
          const element = document.getElementById(targetId);
          if (element) element.scrollIntoView({ behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    }
  };

  const pageMotion = {
    initial: { opacity: 0, y: 15 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -15 },
    transition: { duration: 0.35, ease: "easeInOut" as const },
  };

  return (
    <main className="min-h-screen bg-[#030014] text-white selection:bg-purple-500/30 relative">
      {/* Global Background (Nebula Dark Mode) */}
      <div className="fixed inset-0 z-0 bg-[#030014]">
        {bgReady && (
          <div className="absolute inset-0 animate-in fade-in duration-1000">
            <BubbleBackground
              interactive={false}
              colors={{
                first: "139, 92, 246",
                second: "14, 165, 233",
                third: "167, 139, 250",
                fourth: "236, 72, 153",
                fifth: "124, 58, 237",
                sixth: "139, 92, 246",
              }}
            />
            <div className="absolute inset-0 bg-[#030014]/70 pointer-events-none" />
            <TechSaraf />
          </div>
        )}
      </div>

      <div className="relative z-10">
        <Header currentPage={currentPage} onNavigate={handleNavigate} />

        <AnimatePresence mode="wait" initial={false}>
          {currentPage === "home" && (
            <motion.div key="home" {...pageMotion}>
              <Hero />
              <QuotationFirst />
              <Industries onNavigate={handleNavigate} />
              <QuotationSecond />
              <ProductCarousel />
            </motion.div>
          )}
          {currentPage === "contact" && (
            <motion.div key="contact" {...pageMotion}>
              <Contact />
            </motion.div>
          )}
          {currentPage === "education" && (
            <motion.div key="education" {...pageMotion}>
              <Education />
            </motion.div>
          )}
          {currentPage === "property" && (
            <motion.div key="property" {...pageMotion}>
              <Property />
            </motion.div>
          )}
          {currentPage === "technology" && (
            <motion.div key="technology" {...pageMotion}>
              <Technology />
            </motion.div>
          )}
          {currentPage === "tourism" && (
            <motion.div key="tourism" {...pageMotion}>
              <Tourism />
            </motion.div>
          )}
          {currentPage === "sports" && (
            <motion.div key="sports" {...pageMotion}>
              <Sports />
            </motion.div>
          )}
          {currentPage === "engineering" && (
            <motion.div key="engineering" {...pageMotion}>
              <Engineering />
            </motion.div>
          )}
          {currentPage === "about" && (
            <motion.div key="about" {...pageMotion}>
              <AboutPage />
            </motion.div>
          )}
          {currentPage === "socials" && (
            <motion.div key="socials" {...pageMotion}>
              <SocialMediaPage />
            </motion.div>
          )}
          {currentPage === "dashboard" && (
            <motion.div key="dashboard" {...pageMotion}>
              <DashboardPage />
            </motion.div>
          )}
        </AnimatePresence>

        <Footer onNavigate={handleNavigate} />
      </div>
      <NeedsQuizModal isOpen={isQuizOpen} onClose={() => setIsQuizOpen(false)} />
    </main>
  );
}
